import { ruianSuggestItems, suggestAddress as suggestMapyAddress, type MapySuggestItem } from '@redhand/ui';
import { baseUrl } from './api.js';
import { mapyApiKey } from './mapy.js';

/**
 * Krok NOC3 bod 5 (nalez N17) — NASEPTAVAC ADRESY NEMOVITOSTI JDE PRES RUIAN.
 *
 * QA tester napsal „Náměstí Míru 22, Praha 2" a pruvodce nove zakazky nabidl
 * jen „Praha 2, Praha" — tedy OKRES, ne adresu. Pricina: wizard volal
 * napovedu Mapy.com, zatimco nas narodni registr (`GET /v1/public/
 * address-lookup`, krok C2b) tutez adresu najde vcetne c. p. / c. o.
 * (NOC2 opravil `RuianAddressProvider`, ale wizard k nemu nevedl).
 *
 * Poradi zdroju:
 *  1. RUIAN — TENTYZ registr, nad kterym se pak predmet zakazky overuje,
 *     takze vybrana adresa nemuze „neexistovat" az pri provereni,
 *  2. Mapy.com jen jako ZALOHA, kdyz registr nic nenasel (vypadek sluzby,
 *     adresa mimo CR) — a jeji vysledek se NEPOTVRZUJE jako poloha.
 *
 * Co znamena „nepotvrzuje": navrh bez `position` ulozi pruvodce bez bodu,
 * takze `hasPlace` je `false` a u pole stoji hlaska „vyberte konkrétní
 * adresu" (`wizard-address-unconfirmed`, krok PRF1). Tim nikdy nevznikne
 * zakazka, ktera si mysli, ze zna adresu, a pritom ma okres — presne to
 * vadilo nalezu N17. Rozhodnuti o presnosti dela REGISTR (`approximate`),
 * ne rozpoznavani textu: „Praha 2" a „Vichrova 1914" jsou syntakticky
 * totez (zjisteni NOC2).
 */

/** Odpoved `GET /v1/public/address-lookup` (schema `addressLookupResponseSchema`). */
interface AddressLookupResponse {
  readonly items?: readonly {
    readonly formatted?: string;
    readonly latitude?: string;
    readonly longitude?: string;
    readonly approximate?: boolean;
  }[];
}

/** Registr zacina napovidat od tri znaku (`MIN_QUERY_LENGTH` v commerce). */
const MIN_QUERY_LENGTH = 3;

async function suggestRuian(query: string, signal: AbortSignal): Promise<readonly MapySuggestItem[]> {
  if (query.trim().length < MIN_QUERY_LENGTH) return [];
  try {
    const res = await fetch(`${baseUrl}/v1/public/address-lookup?q=${encodeURIComponent(query.trim())}`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
      signal,
    });
    if (!res.ok) return [];
    const data = (await res.json()) as AddressLookupResponse;
    const hits = (data.items ?? []).flatMap((item) =>
      typeof item.formatted === 'string' && typeof item.latitude === 'string' && typeof item.longitude === 'string'
        ? [{
            formatted: item.formatted,
            latitude: item.latitude,
            longitude: item.longitude,
            approximate: item.approximate === true,
          }]
        : [],
    );
    return withoutApproximatePosition(ruianSuggestItems(hits));
  } catch {
    return [];
  }
}

/**
 * Priblizna shoda (registr nenasel c. p.) zustava VIDET — makler z ni pozna,
 * ze ulice je spravna — ale bez bodu, takze se nepredvyplni jako adresa
 * nemovitosti.
 */
function withoutApproximatePosition(items: readonly MapySuggestItem[]): readonly MapySuggestItem[] {
  return items.map((item) => (item.approximate === true ? { ...item, position: null } : item));
}

/**
 * Napoveda adresy nemovitosti pro pruvodce zakazkou a pro editaci predmetu.
 * Prazdne pole = zadny navrh (i pri chybe site).
 */
export async function suggestPropertyAddress(
  query: string,
  signal: AbortSignal,
  /* `apiKeyOverride` je jen pro testy — Vite nesynchronizuje `import.meta.env`
     s `vi.stubEnv` (tentyz duvod jako v `lib/mapy.ts`). Produkce ho nepredava. */
  apiKeyOverride?: string,
): Promise<readonly MapySuggestItem[]> {
  const fromRegistry = await suggestRuian(query, signal);
  if (fromRegistry.length > 0) return fromRegistry;
  if (signal.aborted) return [];
  const fromMapy = await suggestMapyAddress(query, signal, apiKeyOverride ?? mapyApiKey());
  // Zaloha neni registr: jeji navrh je vodidlo, kam se v mape podivat, ne
  // potvrzena adresa predmetu (viz komentar u modulu).
  return fromMapy.map((item) => ({ ...item, approximate: true, position: null }));
}
